import GithubSlugger from "github-slugger";
import { computed, ref, watch } from "vue";
import { isDefined } from "@scalar/oas-utils/helpers";
import { isConfigurationWithSources, apiReferenceConfigurationWithSourceSchema } from "@scalar/types/api-reference";
const QUERY_PARAMETER = "api";
const slugger = new GithubSlugger();
const normalizeConfigurations = (configuration) => {
  if (!configuration) {
    return [];
  }
  const configs = Array.isArray(configuration) ? configuration : [configuration];
  const sources = configs.flatMap((config) => {
    if (isConfigurationWithSources(config)) {
      const { sources: configSources, ...rest } = config;
      return configSources?.map((source) => ({ ...rest, ...source })) ?? [];
    }
    return [config];
  });
  return sources.map((source, index) => source && addSlugAndTitle(source, index)).filter(isDefined);
};
const addSlugAndTitle = (_source, index = 0) => {
  const source = {
    ..._source,
    // this is before parsing so we transform the old style
    ..._source.spec ?? {}
  };
  if (!source?.url && !source?.content) {
    return void 0;
  }
  slugger.reset();
  if (source.title) {
    return {
      ...source,
      slug: source.slug || slugger.slug(source.title),
      title: source.title
    };
  }
  if (source.slug) {
    return {
      ...source,
      title: source.slug
    };
  }
  return {
    ...source,
    slug: `api-${index + 1}`,
    title: `API #${index + 1}`
  };
};
const useMultipleDocuments = ({
  configuration,
  configurationOverrides,
  initialIndex,
  isIntersectionEnabled,
  hash,
  hashPrefix
}) => {
  const availableDocuments = computed(
    () => normalizeConfigurations(configuration.value)
  );
  const getInitialSelection = () => {
    if (typeof window === "undefined") {
      return typeof initialIndex === "number" ? initialIndex : 0;
    }
    const url = new URL(window.location.href);
    const parameter = url.searchParams.get(QUERY_PARAMETER);
    if (parameter) {
      const indexBySlug = availableDocuments.value.findIndex(
        (option) => option.slug === parameter
      );
      if (indexBySlug !== -1) {
        return indexBySlug;
      }
      const numericIndex = Number.parseInt(parameter, 10);
      if (!isNaN(numericIndex) && numericIndex >= 0 && numericIndex < availableDocuments.value.length) {
        return numericIndex;
      }
    }
    const defaultIndex = availableDocuments.value.findIndex(
      (doc) => "default" in doc && doc.default === true
    );
    if (defaultIndex !== -1) {
      return defaultIndex;
    }
    if (typeof initialIndex === "number") {
      return initialIndex;
    }
    return 0;
  };
  const selectedDocumentIndex = ref(getInitialSelection());
  const selectedConfiguration = computed(
    () => {
      const overrides = configurationOverrides?.value ?? {};
      if (configuration.value && isConfigurationWithSources(configuration.value)) {
        return apiReferenceConfigurationWithSourceSchema.parse({
          ...configuration.value,
          ...configuration.value?.sources?.[selectedDocumentIndex.value],
          ...availableDocuments.value[selectedDocumentIndex.value],
          ...overrides
        });
      }
      const flattenedConfig = [configuration.value].flat()[selectedDocumentIndex.value] ?? {};
      return apiReferenceConfigurationWithSourceSchema.parse({
        ...flattenedConfig,
        ...availableDocuments.value[selectedDocumentIndex.value],
        ...overrides
      });
    }
  );
  watch(
    selectedDocumentIndex,
    (value) => {
      if (typeof window === "undefined") {
        return;
      }
      if (availableDocuments.value.length === 1) {
        return;
      }
      const url = new URL(window.location.href);
      const selectedDefinition = availableDocuments.value[value];
      if (selectedConfiguration.value.pathRouting) {
        url.pathname = selectedConfiguration.value.pathRouting?.basePath ?? "";
      }
      const parameterValue = selectedDefinition?.slug ?? value.toString();
      url.searchParams.set(QUERY_PARAMETER, parameterValue);
      url.hash = "";
      window.history.replaceState({}, "", url.toString());
      hash.value = "";
      hashPrefix.value = "";
      isIntersectionEnabled.value = false;
      if (typeof window !== "undefined") {
        window.scrollTo({ top: 0, behavior: "instant" });
      }
      if (typeof selectedConfiguration.value.onDocumentSelect === "function") {
        selectedConfiguration.value.onDocumentSelect?.();
      }
    },
    { flush: "sync" }
  );
  return {
    selectedConfiguration,
    availableDocuments,
    selectedDocumentIndex,
    isIntersectionEnabled,
    hash,
    hashPrefix
  };
};
export {
  normalizeConfigurations,
  useMultipleDocuments
};
//# sourceMappingURL=use-multiple-documents.js.map
